import { inject, Injectable } from "@angular/core";
import { Action, createSelector, Selector, State, StateContext } from "@ngxs/store";
import { CompoTreeType } from "../../enums/CompoTreeType";
import { OrgLocationClassObjectSelectDialogActions } from "./org-location-class-object-select-dialog.actions";
import {
  OrgLocationClassObjectSelectionMode
} from "../../content/components/org-location-class-object-selection-dialog/org-location-class-object-selection.enum";
import { Dialog2Service } from "../../services/dialog2.service";
import { CompoApp } from "../../services/CompoApp";
import { filter, map, takeUntil } from "rxjs/operators";
import { DynamicFilterRequestDto } from "../../models/dynamic-filter/dynamic-filter-request-dto.model";
import { FilterPageNameEnum } from "../../enums/dynamic-filter/FilterPageNameEnum";
import { Observable } from "rxjs";
import { ResetStateDefaults } from "../base-action-class.model";
import { AuthserviceService } from "../../content/pages-new-design/auth-page/login-page/authservice.service";
import { UnsubscribeBaseServiceModel } from "../../services/client/UnsubscribeBaseService.model";
import { DynamicFilterTemplateService } from '../../services/dynamic-filter-template.service';
import { DynamicFilterTemplatePage } from '../../models/dynamic-filter/dynamic-filter-template-page.model';
import SetStepOneYIndexFor = OrgLocationClassObjectSelectDialogActions.SetOrgLocationYIndexFor;
import RemoveStepOneYIndexFor = OrgLocationClassObjectSelectDialogActions.RemoveOrgLocationYIndexFor;
import SetStepOneSelection = OrgLocationClassObjectSelectDialogActions.SetOrgLocationSelection;
import SetAllOrganizationIds = OrgLocationClassObjectSelectDialogActions.SetAllOrganizationIds;
import SetAllLocationIds = OrgLocationClassObjectSelectDialogActions.SetAllLocationIds;
import Step = OrgLocationClassObjectSelectDialogActions.Step;
import SetAllClassIds = OrgLocationClassObjectSelectDialogActions.SetAllClassIds;
import SetStepTwoSelection = OrgLocationClassObjectSelectDialogActions.SetClassSelection;
import SetStepTwoYIndexFor = OrgLocationClassObjectSelectDialogActions.SetClassYIndexFor;
import RemoveStepTwoYIndexFor = OrgLocationClassObjectSelectDialogActions.RemoveClassYIndexFor;
import SetAllObjectIds = OrgLocationClassObjectSelectDialogActions.SetAllObjectIds;
import CheckFetchObjects = OrgLocationClassObjectSelectDialogActions.CheckFetchObjects;
import OpenDialog = OrgLocationClassObjectSelectDialogActions.OpenDialog;
import SetSelectionMode = OrgLocationClassObjectSelectDialogActions.SetSelectionMode;
import NextStep = OrgLocationClassObjectSelectDialogActions.NextStep;
import PreviousStep = OrgLocationClassObjectSelectDialogActions.PreviousStep;
import SetOrgLocationSelection = OrgLocationClassObjectSelectDialogActions.SetOrgLocationSelection;
import SetClassSelection = OrgLocationClassObjectSelectDialogActions.SetClassSelection;
import SetObjectSelection = OrgLocationClassObjectSelectDialogActions.SetObjectSelection;
import CloseDialog = OrgLocationClassObjectSelectDialogActions.CloseDialog;
import Save = OrgLocationClassObjectSelectDialogActions.Save;
import DispatchSave = OrgLocationClassObjectSelectDialogActions.DispatchSave;
import SaveSuccessful = OrgLocationClassObjectSelectDialogActions.SaveSuccessful;
import SaveFailed = OrgLocationClassObjectSelectDialogActions.SaveFailed;
import { Dialog2Type } from '../../models/dialog/dialog2.model';


export class OrgLocationClassObjectSelectDialogStateModel {
  //region DIALOG
  visible: boolean;
  selectionMode: OrgLocationClassObjectSelectionMode;
  selectionModes: OrgLocationClassObjectSelectionMode[];
  baseOrganizationId: string;
  steps: Step[];
  step: Step;
  stepCounter: number;
  saveInProgress: boolean;
  //endregion

  //region ORG-LOCATION STEP
  stepOneYIndexes: { id: string, parentId: string, type: CompoTreeType, yIndex: number }[] = [];
  allLocationsIds: Set<string>;
  allOrganizationIds: Set<string>;
  selectedLocationIds: Set<string>;
  selectedOrganizationIds: Set<string>;
  //endregion

  //region CLASS STEP
  stepTwoYIndexes: { id: string, parentId: string, yIndex: number }[] = [];
  allClassIds: Set<string>;
  selectedClassIds: Set<string>;
  //endregion

  //region OBJECT STEP
  allObjectIds: Set<string>;
  selectedObjectIds: Set<string>;
  excludedObjectIds: Set<string>;
  //endregion
}

const getDefaults = (): OrgLocationClassObjectSelectDialogStateModel => ({
  visible: false,
  selectionMode: null,
  selectionModes: null,
  baseOrganizationId: null,
  steps: [],
  step: OrgLocationClassObjectSelectDialogActions.Step.ORG_LOCATION,
  stepCounter: 1,
  saveInProgress: false,

  //region ORG-LOCATION STEP
  stepOneYIndexes: [],
  allLocationsIds: new Set(),
  allOrganizationIds: new Set(),
  selectedLocationIds: new Set(),
  selectedOrganizationIds: new Set(),
  //endregion

  //region CLASS STEP
  stepTwoYIndexes: [],
  allClassIds: new Set(),
  selectedClassIds: new Set(),
  //endregion

  //region OBJECT STEP
  allObjectIds: new Set(),
  selectedObjectIds: new Set(),
  excludedObjectIds: null
  //endregion
});

@State<OrgLocationClassObjectSelectDialogStateModel>({
  name: 'OrgLocationClassObjectSelectDialog',
  defaults: getDefaults()
})
@Injectable()
export class OrgLocationClassObjectSelectDialogXsState extends UnsubscribeBaseServiceModel {

  private static objectPageFilter: DynamicFilterTemplatePage;

  private _dynamicFilterTemplateService = inject(DynamicFilterTemplateService);

  constructor(
    private dialog2Service: Dialog2Service,
    private authService: AuthserviceService,
    private app: CompoApp
  ) {
    super();
    if (!OrgLocationClassObjectSelectDialogXsState.objectPageFilter && authService.isAuthenticated()) {
      this._dynamicFilterTemplateService.getByPageEnum(FilterPageNameEnum.OBJECT_PAGE)
        .then(resp => {
          OrgLocationClassObjectSelectDialogXsState.objectPageFilter = resp;
        });
    }

    app.on.loginTrigger.pipe(takeUntil(this.unSubscribe$), filter(() => !OrgLocationClassObjectSelectDialogXsState.objectPageFilter))
      .subscribe(() => {
        this._dynamicFilterTemplateService.getByPageEnum(FilterPageNameEnum.OBJECT_PAGE)
          .then(resp => {
            OrgLocationClassObjectSelectDialogXsState.objectPageFilter = resp;
          });
      });
  }

  //region SELECTORS
  // @ts-ignore
  @Selector()
  static getOrgLocationYIndex(id: string, type: CompoTreeType) {
    return createSelector([this], (state): number => {
      const yIndexes = [...state.userAreaMappingImportDialog.stepOneYIndexes];
      const indexOfEntry = yIndexes.findIndex(yIndex => yIndex.id === id && yIndex.type === type);
      if (indexOfEntry !== -1) {
        return yIndexes
          .slice(0, indexOfEntry + 1)
          .map(yIndex => yIndex.yIndex)
          .reduce((prev, curr) => prev + curr, 0);
      }
      return 0;
    });
  }

  @Selector()
  static getClassYIndex(id: string) {
    return createSelector([this], (state): number => {
      const yIndexes = [...state.userAreaMappingImportDialog.stepTwoYIndexes];
      const indexOfEntry = yIndexes.findIndex(yIndex => yIndex.id === id);
      if (indexOfEntry !== -1) {
        return yIndexes
          .slice(0, indexOfEntry + 1)
          .map(yIndex => yIndex.yIndex)
          .reduce((prev, curr) => prev + curr, 0);
      }
      return 0;
    });
  }

  @Selector()
  static visible(state: OrgLocationClassObjectSelectDialogStateModel): boolean {
    return state.visible;
  }

  @Selector()
  static step(state: OrgLocationClassObjectSelectDialogStateModel): Step {
    return state.step;
  }

  @Selector()
  static nextStepOrSaveIsEnabled(state: OrgLocationClassObjectSelectDialogStateModel): boolean {
    if (state.saveInProgress) {
      return false;
    }

    let selectionIsSet = false;
    switch (state.step) {
      case OrgLocationClassObjectSelectDialogActions.Step.SELECT_MODE: {
        selectionIsSet = !!state.selectionMode;
        break;
      }
      case OrgLocationClassObjectSelectDialogActions.Step.ORG_LOCATION: {
        selectionIsSet = state.selectedOrganizationIds.size + state.selectedLocationIds.size > 0;
        break;
      }
      case OrgLocationClassObjectSelectDialogActions.Step.CLASS: {
        selectionIsSet = state.selectedClassIds.size > 0;
        break;
      }
      case OrgLocationClassObjectSelectDialogActions.Step.OBJECT: {
        selectionIsSet = state.selectedObjectIds.size > 0;
        break;
      }
    }

    return selectionIsSet;
  }

  @Selector()
  static nextStepPossible(state: OrgLocationClassObjectSelectDialogStateModel): boolean {
    const currentStepIndex = state.steps.indexOf(state.step);
    return currentStepIndex < state.steps.length - 1 || state.step === Step.SELECT_MODE;
  }

  @Selector()
  static savePossible(state: OrgLocationClassObjectSelectDialogStateModel): boolean {
    const currentStepIndex = state.steps.indexOf(state.step);
    return currentStepIndex === state.steps.length - 1 && state.step !== Step.SELECT_MODE;
  }

  @Selector()
  static previousStepPossible(state: OrgLocationClassObjectSelectDialogStateModel): boolean {
    return state.steps.indexOf(state.step) > 0;
  }

  @Selector()
  static stepCounter(state: OrgLocationClassObjectSelectDialogStateModel): number {
    return state.stepCounter;
  }

  @Selector()
  static allSteps(state: OrgLocationClassObjectSelectDialogStateModel): Step[] {
    return state.steps;
  }

  @Selector()
  static selectionMode(state: OrgLocationClassObjectSelectDialogStateModel): OrgLocationClassObjectSelectionMode {
    return state.selectionMode;
  }

  @Selector()
  static selectionModes(state: OrgLocationClassObjectSelectDialogStateModel): OrgLocationClassObjectSelectionMode[] {
    return state.selectionModes;
  }

  @Selector()
  static baseOrganizationId(state: OrgLocationClassObjectSelectDialogStateModel): string {
    return state.baseOrganizationId;
  }

  @Selector()
  static state(state: OrgLocationClassObjectSelectDialogStateModel): OrgLocationClassObjectSelectDialogStateModel {
    return state;
  }

  @Selector()
  static saveInProgress(state: OrgLocationClassObjectSelectDialogStateModel): boolean {
    return state.saveInProgress;
  }

  @Selector()
  static selectedLocationIds(state: OrgLocationClassObjectSelectDialogStateModel): Set<string> {
    return state.selectedLocationIds;
  }

  @Selector()
  static selectedOrganizationIds(state: OrgLocationClassObjectSelectDialogStateModel): Set<string> {
    return state.selectedOrganizationIds;
  }

  @Selector()
  static selectedClassIds(state: OrgLocationClassObjectSelectDialogStateModel): Set<string> {
    return state.selectedClassIds;
  }

  @Selector()
  static selectedObjectIds(state: OrgLocationClassObjectSelectDialogStateModel): Set<string> {
    return state.selectedObjectIds;
  }

  @Selector()
  static allLocationIds(state: OrgLocationClassObjectSelectDialogStateModel): Set<string> {
    return state.allLocationsIds;
  }

  @Selector()
  static allOrganizationIds(state: OrgLocationClassObjectSelectDialogStateModel): Set<string> {
    return state.allOrganizationIds;
  }

  @Selector()
  static allClassIds(state: OrgLocationClassObjectSelectDialogStateModel): Set<string> {
    return state.allClassIds;
  }

  @Selector()
  static allObjectIds(state: OrgLocationClassObjectSelectDialogStateModel): Set<string> {
    return state.allObjectIds;
  }

  @Selector()
  static excludedObjectIds(state: OrgLocationClassObjectSelectDialogStateModel): Set<string> {
    return state.excludedObjectIds;
  }
  //endregion SELECTORS

  //region ACTIONS

  //region DIALOG
  @Action(OpenDialog)
  public openDialog(ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>, action: OpenDialog) {
    if (ctx.getState().saveInProgress) {
      throw new Error('Save is in progress!');
    }

    if (!action.predefinedSelectionMode && !action.selectionModes) {
      throw new Error("Both 'predefinedSelectionMode' and 'selectionModes' are null!");
    }

    if (action.predefinedSelectionMode && action.selectionModes) {
      throw new Error("If 'predefinedSelectionMode' is set then 'selectionModes' have to be null!");
    }

    if (!action.organizationId) {
      throw new Error("OrganizationId cannot be null!");
    }

    let steps: Step[];
    let selectionMode = null;
    if (action.predefinedSelectionMode) {
      selectionMode = action.predefinedSelectionMode;
      steps = OrgLocationClassObjectSelectDialogXsState.getStepsForSelectionMode(action.predefinedSelectionMode);
    } else {
      steps = [Step.SELECT_MODE];
    }

    ctx.patchState({
      visible: true,
      selectionMode,
      selectionModes: action.selectionModes,
      baseOrganizationId: action.organizationId,
      steps: steps,
      step: steps[0],
      stepCounter: 1,
      excludedObjectIds: action.excludedObjectIds ? new Set(action.excludedObjectIds) : null
    });
    this.dialog2Service.openDialog(Dialog2Type.ORG_LOCATION_CLASS_OBJECT_SELECT_DIALOG);
  }

  @Action(CloseDialog)
  public closeDialog(ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>): void {
    if (ctx.getState().saveInProgress) {
      throw new Error('Save is in progress!');
    }

    ctx.setState({...getDefaults()});
    this.dialog2Service.closeDialog(Dialog2Type.ORG_LOCATION_CLASS_OBJECT_SELECT_DIALOG);
  }

  @Action(SetSelectionMode)
  public setSelectionMode(
    ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>,
    action: SetSelectionMode
  ) {
    if (ctx.getState().selectionMode === action.selectionMode) {
      return;
    }

    let steps: Step[];
    if (action.selectionMode) {
      steps = [
        Step.SELECT_MODE,
        ...OrgLocationClassObjectSelectDialogXsState.getStepsForSelectionMode(action.selectionMode)
      ];
    } else {
      steps = [Step.SELECT_MODE];
    }

    ctx.patchState({
      selectionMode: action.selectionMode,
      step: action.selectionMode ? ctx.getState().step : Step.SELECT_MODE,
      steps: steps,
    });
    ctx.dispatch([
      new SetOrgLocationSelection(new Set(), new Set()),
      new SetClassSelection(new Set()),
      new SetObjectSelection(new Set())
    ]);
  }

  @Action(NextStep)
  public nextStep(ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>): void {
    if (ctx.getState().saveInProgress) {
      throw new Error('Save is in progress!');
    }

    const steps = ctx.getState().steps;
    const currentStepIndex = steps.indexOf(ctx.getState().step);
    if (currentStepIndex < steps.length - 1) {
      ctx.patchState({
        step: steps[currentStepIndex + 1],
        stepCounter: ctx.getState().stepCounter + 1
      });
      ctx.dispatch(CheckFetchObjects);
    }
  }

  @Action(PreviousStep)
  public previousStep(ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>): void {
    if (ctx.getState().saveInProgress) {
      throw new Error('Save is in progress!');
    }

    const steps = ctx.getState().steps;
    const currentStepIndex = steps.indexOf(ctx.getState().step);
    if (currentStepIndex > 0) {
      ctx.patchState({
        step: steps[currentStepIndex - 1],
        stepCounter: ctx.getState().stepCounter - 1
      });
    }
  }
  //endregion

  //region ORG-LOCATION STEP
  @Action(SetStepOneYIndexFor)
  public setOrgLocationYIndexFor(
    ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>,
    action: SetStepOneYIndexFor
  ) {
    const id = action.id, parentId = action.parentId, type = action.type,
      yIndexes = [...ctx.getState().stepOneYIndexes],
      yIndex = yIndexes.find(yIndex => yIndex.id === id && yIndex.type === type);
    let index = action.index;

    if (index === -1) {
      index = null;
    }

    if (yIndex) {
      const childIndex = yIndexes.findIndex(e => e.id === id && e.type === type);
      if (childIndex !== -1) {
        yIndexes.splice(childIndex, 1, {id, parentId, type, yIndex: index});
      } else {
        yIndexes.push({id, type, parentId, yIndex: index});
      }
    } else {
      const childIndex = yIndexes.findIndex(e => e.parentId === id && e.type === type);
      if (childIndex !== -1) {
        yIndexes.splice(childIndex, 0, {id, parentId, type: type, yIndex: index});
      } else {
        yIndexes.push({id, type, parentId, yIndex: index});
      }
    }

    ctx.patchState({
      stepOneYIndexes: yIndexes
    });
  }

  @Action(RemoveStepOneYIndexFor)
  public removeOrgLocationYIndexFor(
    ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>,
    action: RemoveStepOneYIndexFor
  ) {
    let yIndexes = [...ctx.getState().stepOneYIndexes];
    const id = action.id, type = action.type,
      yIndexIdx = yIndexes.findIndex(yIndex => yIndex.id === id && yIndex.type === type);


    if (yIndexIdx !== -1) {
      yIndexes = yIndexes.slice(0, yIndexIdx);
    }

    ctx.patchState({
      stepOneYIndexes: yIndexes
    });
  }

  @Action(SetStepOneSelection)
  public setOrgLocationSelection(ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>, action: SetStepOneSelection) {
    if (ctx.getState().saveInProgress) {
      throw new Error('Save is in progress!');
    }

    ctx.patchState({
      selectedOrganizationIds: action.organizationIds,
      selectedLocationIds: action.locationIds
    });
  }

  @Action(SetAllOrganizationIds)
  public setAllOrganizationIds(ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>, action: SetAllOrganizationIds) {
    ctx.patchState({
      allOrganizationIds: action.organizationIds
    });
  }

  @Action(SetAllLocationIds)
  public setAllLocationIds(ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>, action: SetAllLocationIds) {
    ctx.patchState({
      allLocationsIds: action.locationIds
    });
  }
  //endregion

  //region CLASS STEP
  @Action(SetStepTwoYIndexFor)
  public setClassYIndexFor(ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>, action: SetStepTwoYIndexFor) {
    const id = action.id, parentId = action.parentId,
      yIndexes = [...ctx.getState().stepTwoYIndexes],
      yIndex = yIndexes.find(yIndex => yIndex.id === id);
    let index = action.index;

    if (index === -1) {
      index = null;
    }

    if (yIndex) {
      const childIndex = yIndexes.findIndex(e => e.id === id);
      if (childIndex !== -1) {
        yIndexes.splice(childIndex, 1, {id, parentId, yIndex: index});
      } else {
        yIndexes.push({id, parentId, yIndex: index});
      }
    } else {
      const childIndex = yIndexes.findIndex(e => e.parentId === id);
      if (childIndex !== -1) {
        yIndexes.splice(childIndex, 0, {id, parentId, yIndex: index});
      } else {
        yIndexes.push({id, parentId, yIndex: index});
      }
    }

    ctx.patchState({
      stepTwoYIndexes: yIndexes
    });
  }

  @Action(RemoveStepTwoYIndexFor)
  public removeClassYIndexFor(ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>, action: RemoveStepTwoYIndexFor) {
    let yIndexes = [...ctx.getState().stepOneYIndexes];
    const id = action.id,
      yIndexIdx = yIndexes.findIndex(yIndex => yIndex.id === id);


    if (yIndexIdx !== -1) {
      yIndexes = yIndexes.slice(0, yIndexIdx);
    }

    ctx.patchState({
      stepTwoYIndexes: yIndexes
    });
  }

  @Action(SetStepTwoSelection)
  public setClassSelection(ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>, action: SetStepTwoSelection) {
    if (ctx.getState().saveInProgress) {
      throw new Error('Save is in progress!');
    }

    ctx.patchState({
      selectedClassIds: action.classIds,
    });
  }

  @Action(SetAllClassIds)
  public setAllClassIds(ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>, action: SetAllClassIds) {
    ctx.patchState({
      allClassIds: action.classIds
    });
  }
  //endregion

  //region OBJECT STEP
  @Action(SetObjectSelection)
  public setObjectSelection(ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>, action: SetObjectSelection) {
    if (ctx.getState().saveInProgress) {
      throw new Error('Save is in progress!');
    }

    ctx.patchState({
      selectedObjectIds: action.objectIds,
    });
  }

  @Action(SetAllObjectIds)
  public setAllObjectIds(ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>, action: SetAllObjectIds) {
    ctx.patchState({
      allObjectIds: action.objectIds
    });
  }

  @Action(CheckFetchObjects)
  public checkFetchObjects(ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>): Observable<void> {
    if (!OrgLocationClassObjectSelectDialogXsState.objectPageFilter || !(ctx.getState().step === Step.OBJECT)) {
      return;
    }

    const dto: DynamicFilterRequestDto
      = OrgLocationClassObjectSelectDialogXsState.generateFilterDto(ctx.getState(), null);

    return this._dynamicFilterTemplateService.searchIdsWithFilterDtoRx(dto)
      .pipe(map((resp) => {
        ctx.patchState({
          allObjectIds: new Set(resp.allIds),
        });
      }));
  }
  //endregion

  //region SAVE
  @Action(Save)
  public save(ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>): void {
    const state = ctx.getState();
    if (OrgLocationClassObjectSelectDialogXsState.nextStepOrSaveIsEnabled(state) && !state.saveInProgress) {
      ctx.dispatch(new DispatchSave(
        new Set(state.selectedOrganizationIds),
        new Set(state.selectedLocationIds),
        new Set(state.selectedClassIds),
        new Set(state.selectedObjectIds)
      ));
      ctx.patchState({
        saveInProgress: true
      });
    }
  }

  @Action(SaveSuccessful)
  public saveSuccessful(ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>): void {
    ctx.patchState({
      saveInProgress: false
    });
    ctx.dispatch(new CloseDialog());
  }

  @Action(SaveFailed)
  public saveFailed(ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>): void {
    ctx.patchState({
      saveInProgress: false
    });
  }
  //endregion

  //region RESET
  @Action(ResetStateDefaults)
  public resetDefaults(ctx: StateContext<OrgLocationClassObjectSelectDialogStateModel>): void {
    ctx.setState({...getDefaults()});
  }
  //endregion

  //endregion ACTIONS

  //region UTILS
  private static getStepsForSelectionMode(selectionMode: OrgLocationClassObjectSelectionMode): Step[] {
    if (!selectionMode) {
      throw Error("SelectionMode cannot be null!");
    }

    switch (selectionMode) {
      case OrgLocationClassObjectSelectionMode.ALL_COMBINED:
        return [Step.ORG_LOCATION, Step.CLASS, Step.OBJECT];
      case OrgLocationClassObjectSelectionMode.ORG_LOCATION_AND_CLASS:
        return [Step.ORG_LOCATION, Step.CLASS];
      case OrgLocationClassObjectSelectionMode.ORG_LOCATION_ONLY:
        return [Step.ORG_LOCATION];
      case OrgLocationClassObjectSelectionMode.CLASS_ONLY:
        return [Step.CLASS];
      case OrgLocationClassObjectSelectionMode.OBJECT_ONLY:
        return [Step.OBJECT];
    }
  }

  public static generateFilterDto(
    state: OrgLocationClassObjectSelectDialogStateModel,
    searchTerm: string
  ): DynamicFilterRequestDto {
    const objectPageFilter = OrgLocationClassObjectSelectDialogXsState.objectPageFilter;
    if (!objectPageFilter?.id) {
      return null;
    }

    const dto = new DynamicFilterRequestDto();

    let orgIds: string[];
    let locationIds: string[];
    let classIds: string[];
    const excludedObjectIds: string[] = state.excludedObjectIds ? [...state.excludedObjectIds] : null;

    if (state.selectionMode === OrgLocationClassObjectSelectionMode.OBJECT_ONLY) {
      orgIds = [state.baseOrganizationId];
      locationIds = [];
      dto.addAllLocationIds = true;
    }

    if (state.selectionMode === OrgLocationClassObjectSelectionMode.ALL_COMBINED) {
      orgIds = [...state.selectedOrganizationIds];
      locationIds = [...state.selectedLocationIds];
      classIds = [...state.selectedClassIds];
    }

    dto.templateId = objectPageFilter.id;
    const filterValues = {}, filterRangeValues = {}, filterAttributeValues = {};
    const classFilter = objectPageFilter.filterEntries.COMPONENT_CLASS;
    const searchTermFilter = objectPageFilter.filterEntries.SEARCH_TERM;

    if (classFilter && classIds && classIds.length > 0) {
      filterValues[classFilter.id] = [...classIds];
    }

    if (searchTermFilter && !!searchTerm) {
      filterValues[searchTermFilter.id] = searchTerm;
    }

    if (excludedObjectIds && excludedObjectIds.length > 0) {
      dto.excludedIds = [...excludedObjectIds];
    }

    dto.filterValues = DynamicFilterRequestDto
      .initFilterValues(objectPageFilter.filterEntries, filterValues);
    dto.filterRangeValues = DynamicFilterRequestDto
      .initFilterRangeValues(objectPageFilter.filterEntries, filterRangeValues);
    dto.filterAttributeValues
      = DynamicFilterRequestDto.initFilterAttributeValues(objectPageFilter.filterEntries, filterAttributeValues);

    dto.orgIds = [...orgIds];
    dto.locationIds = [...locationIds];
    return dto;
  }

  public static objectsFilterLoaded(): boolean {
    return !!OrgLocationClassObjectSelectDialogXsState.objectPageFilter;
  }
  //endregion
}
